import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Stack,
  Alert,
  Button,
  Avatar,
  Tabs,
  Tab,
  Drawer,
  Divider,
  Grid,
} from '@mui/material';
import {
  CalendarMonth,
  CalendarToday,
  AccessTime,
  People,
  Close,
} from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as HeaderCalendarIcon } from '../assets/icons/header-calendar-nav.svg';
import {
  format,
  addDays,
  isSameDay,
  isToday,
  isTomorrow,
} from 'date-fns';
import { ja } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { RankStarInline } from '../components/RankStar';
import api, { crewApi } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';

interface JobShift {
  id: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  break_duration_minutes?: number;
  required_workers: number;
  current_applicants: number;
  job_title?: string;
  job_description?: string;
  position_id?: string;
  memo?: string;
  is_active: boolean;
  salary_amount?: number;
  salary_allowance?: string;
  salary_transport?: string;
  applicants?: Applicant[];
}

interface Applicant {
  id: string;
  user_id: string;
  store_user_id: string;
  name: string;
  skill_level?: string;
  applied_at?: string;
  status?: string;
}

// ランク（スキルレベル）の色・ラベルは constants/rankColors.ts を単一の真実とし、
// RankStarInline 経由で解決する（旧ローカル SKILL_LEVELS の MUI 既定色マップは廃止）。

// 育成アドバイスバッジの配色は CrewManagement.tsx と統一
const ADVICE_BADGE_PURPLE = '#673FC8';
const ADVICE_BADGE_GRADIENT = 'linear-gradient(90deg, #7e318e 0%, #534dff 100%)';

const calcWorkingHours = (startTime?: string, endTime?: string): string => {
  if (!startTime || !endTime) return '--h';
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  let diffMin = (eh * 60 + em) - (sh * 60 + sm);
  if (diffMin <= 0) diffMin += 24 * 60; // 日付跨ぎ対応
  const hours = diffMin / 60;
  return `${hours.toFixed(1)}h`;
};

const formatCurrency = (value?: string | number): string => {
  if (value === undefined || value === null || value === '') return '-';
  const normalized = typeof value === 'number' ? String(value) : value.trim();
  if (/^\d+$/.test(normalized)) {
    return `¥${parseInt(normalized, 10).toLocaleString()}`;
  }
  return normalized;
};

const WorkSchedulePage: React.FC = () => {
  const { storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  const token = managementToken || localStorage.getItem('management_token') || '';
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [shifts, setShifts] = useState<JobShift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState<JobShift | null>(null);
  const [adviceCountMap, setAdviceCountMap] = useState<Map<string, number>>(new Map());

  // 今日から7日間の日付を生成
  const dates = Array.from({ length: 7 }, (_, i) => addDays(new Date(), i));

  useEffect(() => {
    fetchShifts(selectedDate);
  }, [selectedDate, storeId]);

  // クルーごとの有効な育成アドバイス件数（クルー管理画面と同じ集計方法）
  useEffect(() => {
    if (!storeId) {
      setAdviceCountMap(new Map());
      return;
    }
    crewApi.getCopilotAdvice(storeId, token, { status: 'active' })
      .then((adviceList) => {
        const map = new Map<string, number>();
        adviceList.forEach((a) => {
          if (a.store_user_id) {
            map.set(a.store_user_id, (map.get(a.store_user_id) ?? 0) + 1);
          }
        });
        setAdviceCountMap(map);
      })
      .catch(() => setAdviceCountMap(new Map()));
  }, [storeId, token]);

  const getAdviceCount = (storeUserId: string): number => adviceCountMap.get(storeUserId) ?? 0;

  const fetchShifts = async (date: Date) => {
    if (!storeId) return;

    try {
      setLoading(true);
      setError(null);
      const dateStr = format(date, 'yyyy-MM-dd');

      const data = await api.jobShift.getWithApplicants(storeId, {
        from_date: dateStr,
        to_date: dateStr,
        limit: 100,
      });

      setShifts(data.shifts);
    } catch (err: any) {
      console.error('Failed to fetch shifts:', err);
      setError('シフトの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getDateLabel = (date: Date) => {
    if (isToday(date)) {
      return '今日';
    } else if (isTomorrow(date)) {
      return '明日';
    } else if (isSameDay(date, addDays(new Date(), 2))) {
      return '明後日';
    }
    return '';
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
  };

  const handleShiftDetail = (shiftId: string) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (shift) {
      setSelectedShift(shift);
      setDetailDrawerOpen(true);
    }
  };

  const handleCloseDetailDrawer = () => {
    setDetailDrawerOpen(false);
    setSelectedShift(null);
  };

  if (loading && shifts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8 }}>
      <PageHeader
        title="就業予定表"
        icon={<SvgIcon component={HeaderCalendarIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
      />

      {/* 日付タブ */}
      <Box
        sx={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            px: 1.5,
            py: 1,
            width: '100%',
          }}
        >
          {dates.map((date, index) => {
            const isSelected = isSameDay(date, selectedDate);
            const label = getDateLabel(date);
            const dayNum = format(date, 'd', { locale: ja });
            const dayOfWeek = format(date, 'E', { locale: ja });

            return (
              <Card
                key={index}
                onClick={() => handleDateSelect(date)}
                sx={{
                  minWidth: 0,
                  flex: '1 1 0',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  borderRadius: '8px',
                  bgcolor: isSelected ? '#2b333d' : 'background.paper',
                  color: isSelected ? 'white' : 'text.tertiary',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    textAlign: 'center',
                    py: 0.5,
                    px: 0.5,
                    '&:last-child': { pb: 0.5 },
                  }}
                >
                  <Typography
                    variant="caption"
                    display="block"
                    fontWeight="bold"
                    sx={{ mb: 0, minHeight: '14px', fontSize: '0.65rem', lineHeight: 1.2 }}
                  >
                    {label || '\u00A0'}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.0, mb: -0.25 }}>
                    {dayNum}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1 }}>{dayOfWeek}</Typography>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>

      {/* シフトリスト */}
      <Box sx={{ px: 1.5, py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {shifts.length === 0 && !loading && (
          <Alert severity="info">
            {format(selectedDate, 'M月d日(E)', { locale: ja })}の予定はありません
          </Alert>
        )}

        {shifts.map((shift) => (
          <Box key={shift.id} sx={{ mb: 2 }}>
            {/* 求人カード */}
            <Card
              variant="outlined"
              sx={{
                mb: 1,
                border: '1px solid #e9ecef',
                borderRadius: '14px',
              }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack spacing={1.5}>
                  {/* 上部：日付とステータス */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CalendarToday sx={{ fontSize: 14 }} color="action" />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {format(new Date(shift.shift_date), 'yyyy.MM.dd(E)', { locale: ja })}
                      </Typography>
                    </Stack>
                    <Chip
                      label={shift.is_active ? '募集中' : '停止中'}
                      size="small"
                      sx={{
                        bgcolor: shift.is_active ? '#ccf0eb' : '#e5e7eb',
                        color: shift.is_active ? '#0d9488' : '#6b7280',
                        fontWeight: 'bold',
                        fontSize: '0.7rem',
                        height: 22,
                      }}
                    />
                  </Stack>

                  {/* 中部：シフト時間帯ボックス + 求人タイトル */}
                  <Grid container spacing={1}>
                    {/* 左側：シフト時間帯ボックス */}
                    <Grid item xs="auto" sm="auto" sx={{ pt: '0 !important', pl: '0 !important' }}>
                      <Box
                        sx={{
                          bgcolor: '#f3f4f6',
                          borderRadius: 2,
                          py: { xs: 0.75, sm: 1.0 },
                          px: { xs: 1.5, sm: 2.0 },
                          textAlign: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.2 }}
                        >
                          {shift.start_time || '--:--'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ my: '6px', lineHeight: 0, display: 'block', fontSize: { xs: '0.6rem', sm: '0.65rem' } }}>
                          ▼
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.2 }}
                        >
                          {shift.end_time || '--:--'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mt: 0.2, display: 'block', fontSize: { xs: '0.75rem', sm: '0.9rem' }, lineHeight: 1 }}>
                          {calcWorkingHours(shift.start_time, shift.end_time)}
                        </Typography>
                      </Box>
                    </Grid>

                    {/* 右側：求人タイトル + メモ */}
                    <Grid item xs={9} sm={9.5} sx={{ pt: '0 !important' }}>
                      <Typography variant="body1" fontWeight="bold" color={'#3c3c3c'} sx={{ fontSize: { xs: '0.85rem', sm: '1.125rem' } }}>
                        {shift.job_title || '求人'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          mt: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          fontSize: '0.75rem',
                        }}
                      >
                        募集メモ: {shift.memo || ['元気な方歓迎！未経験OK', 'ランチタイム急募！まかない付き', '週末のみOK！高時給'][shifts.indexOf(shift) % 3]}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* 詳細ボタン（右寄せ・コンパクト／ソフトブルー：HTMLデザイン準拠） */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleShiftDetail(shift.id)}
                      sx={{
                        bgcolor: '#e8f1fd',
                        color: '#0071e3',
                        borderRadius: '10px',
                        fontWeight: 'bold',
                        py: 0.5,
                        px: 2.5,
                        fontSize: '0.8rem',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#d6e8fb', boxShadow: 'none' },
                      }}
                    >
                      詳細
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* クルー名称一覧（カード外・直下） */}
            {shift.applicants && shift.applicants.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {shift.applicants.map((applicant) => (
                  <Card
                    key={applicant.id}
                    variant="outlined"
                    onClick={() => navigate(`/crew-management/${applicant.store_user_id}`)}
                    sx={{
                      borderRadius: '13px',
                      border: '1px solid #cfe0f5',
                      cursor: 'pointer',
                      transition: 'background-color .15s',
                      '&:hover': { bgcolor: '#f5f9ff' },
                    }}
                  >
                    <CardContent
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:last-child': { pb: 1.5 },
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary"
                        >
                          {applicant.name}さん
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          {getAdviceCount(applicant.store_user_id) > 0 && (
                            <>
                              <AutoAwesomeIcon sx={{ color: ADVICE_BADGE_PURPLE, fontSize: 16 }} />
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: ADVICE_BADGE_GRADIENT,
                                  color: '#fff',
                                  borderRadius: '50%',
                                  minWidth: 20,
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 700,
                                  px: 0.5,
                                }}
                              >
                                {getAdviceCount(applicant.store_user_id)}
                              </Box>
                            </>
                          )}
                          {applicant.skill_level && (
                            <RankStarInline tierCode={applicant.skill_level} size={18} />
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* シフト詳細Drawer */}
      <Drawer
        anchor="right"
        open={detailDrawerOpen}
        onClose={handleCloseDetailDrawer}
        PaperProps={{
          sx: { width: { xs: '100%', sm: '450px' } }
        }}
      >
        {selectedShift && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* ヘッダー */}
            <Box
              sx={{
                p: 2,
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                シフト詳細
              </Typography>
              <IconButton onClick={handleCloseDetailDrawer} size="small">
                <Close />
              </IconButton>
            </Box>

            {/* コンテンツ */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3, pb: { xs: 5, sm: 3 } }}>
              <Stack spacing={3}>
                {/* 日付 */}
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    日付
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CalendarMonth color="primary" />
                    <Typography variant="body1" fontWeight="bold">
                      {format(new Date(selectedShift.shift_date), 'yyyy年MM月dd日(E)', { locale: ja })}
                    </Typography>
                  </Stack>
                </Box>

                <Divider />

                {/* 求人タイトル */}
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    求人
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedShift.job_title || '求人'}
                  </Typography>
                </Box>

                <Divider />

                {/* 時間帯 */}
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    時間帯
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTime color="action" />
                    <Typography variant="body1" fontWeight="bold">
                      {selectedShift.start_time} 〜 {selectedShift.end_time}
                    </Typography>
                  </Stack>
                  {selectedShift.break_duration_minutes != null && selectedShift.break_duration_minutes > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 3.5 }}>
                      休憩: {selectedShift.break_duration_minutes}分
                    </Typography>
                  )}
                </Box>

                <Divider />

                {/* 給与情報 */}
                {(selectedShift.salary_amount || selectedShift.salary_allowance || selectedShift.salary_transport) && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        給与情報
                      </Typography>
                      <Stack spacing={0.5}>
                        {selectedShift.salary_amount && (
                          <Typography variant="body1">
                            時給: <Box component="span" fontWeight="bold">{formatCurrency(selectedShift.salary_amount)}</Box>
                          </Typography>
                        )}
                        {selectedShift.salary_allowance && (
                          <Typography variant="body2" color="text.secondary">
                            手当: {formatCurrency(selectedShift.salary_allowance)}
                          </Typography>
                        )}
                        {selectedShift.salary_transport && (
                          <Typography variant="body2" color="text.secondary">
                            交通費: {formatCurrency(selectedShift.salary_transport)}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <Divider />
                  </>
                )}

                {/* 業務内容（全文表示） */}
                {selectedShift.job_description && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        業務内容
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {selectedShift.job_description}
                      </Typography>
                    </Box>
                    <Divider />
                  </>
                )}

                <Divider />

                {/* 募集人数 */}
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    募集状況
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <People color="action" />
                    <Typography variant="body1">
                      <Box component="span" fontWeight="bold" color="primary.main">
                        {selectedShift.current_applicants}
                      </Box>
                      {' / '}
                      {selectedShift.required_workers}名
                    </Typography>
                    {selectedShift.current_applicants >= selectedShift.required_workers && (
                      <Chip label="満員" size="small" color="error" />
                    )}
                  </Stack>
                </Box>

                {selectedShift.memo && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" gutterBottom>
                        募集メモ
                      </Typography>
                      <Typography variant="body1">
                        {selectedShift.memo}
                      </Typography>
                    </Box>
                  </>
                )}

                <Divider />

                {/* 応募者リスト */}
                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    応募済みクルー ({selectedShift.applicants?.length || 0}名)
                  </Typography>
                  <Stack spacing={1.5} mt={2}>
                    {selectedShift.applicants && selectedShift.applicants.length > 0 ? (
                      selectedShift.applicants.map((applicant) => {
                        return (
                          <Card
                            key={applicant.id}
                            variant="outlined"
                            onClick={() => navigate(`/crew-management/${applicant.store_user_id}`)}
                            sx={{
                              cursor: 'pointer',
                              transition: 'background-color .15s',
                              '&:hover': { bgcolor: '#f5f9ff' },
                            }}
                          >
                            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                                <Box>
                                  <Typography variant="body1" fontWeight="bold">
                                    {applicant.name}
                                  </Typography>
                                  {applicant.applied_at && (
                                    <Typography variant="caption" color="text.secondary">
                                      応募日時: {format(new Date(applicant.applied_at), 'MM/dd HH:mm', { locale: ja })}
                                    </Typography>
                                  )}
                                </Box>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  {applicant.status && (
                                    <Chip
                                      label={
                                        applicant.status === 'accepted' ? '承認済' :
                                        applicant.status === 'submitted' ? '応募中' :
                                        applicant.status === 'rejected' ? '却下' : applicant.status
                                      }
                                      size="small"
                                      color={
                                        applicant.status === 'accepted' ? 'success' :
                                        applicant.status === 'submitted' ? 'info' :
                                        applicant.status === 'rejected' ? 'error' : 'default'
                                      }
                                      sx={{ mr: 0.5 }}
                                    />
                                  )}
                                  {getAdviceCount(applicant.store_user_id) > 0 && (
                                    <>
                                      <AutoAwesomeIcon sx={{ color: ADVICE_BADGE_PURPLE, fontSize: 16 }} />
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          background: ADVICE_BADGE_GRADIENT,
                                          color: '#fff',
                                          borderRadius: '50%',
                                          minWidth: 20,
                                          height: 20,
                                          fontSize: '0.65rem',
                                          fontWeight: 700,
                                          px: 0.5,
                                        }}
                                      >
                                        {getAdviceCount(applicant.store_user_id)}
                                      </Box>
                                    </>
                                  )}
                                  {applicant.skill_level && (
                                    <RankStarInline tierCode={applicant.skill_level} size={20} />
                                  )}
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        );
                      })
                    ) : (
                      <Alert severity="info">まだ応募がありません</Alert>
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default WorkSchedulePage;
